import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import PageIntro from '../components/PageIntro';
import PageSection from '../components/page/PageSection';
import Eyebrow from '../components/page/Eyebrow';
import StepNumber from '../components/page/StepNumber';
import Icon from '../components/Icon';
import { tie } from '../components/page/typography';
import { SITE } from '../components/Seo';
import { POSTAL_ADDRESS } from '../content/contacts';
import { companyLead, vision, mission, values, howWeWork, whyUs, team } from '../content/company';

/**
 * «Чому саме ми» без першого пункту. «Медичне право — наша основна
 * спеціалізація…» тут уже сказано тричі: у заголовку сторінки й в абзаці
 * вступу просто над переліком. На головній пункт лишається — там заголовка
 * про спеціалізацію немає.
 *
 * Іконки за змістом пункту, а не п'ять однакових галочок, і в тому самому
 * стилі, що в цінностей нижче. Порядок іконок відповідає порядку whyUs.
 *
 * Іконки звірені з «Ціновою політикою»: там стрілки до центру — разова
 * послуга, а рукостискання — формат співпраці. Тут вони означали б інше,
 * тож «усі напрямки» — схема напрямків (та сама, що в «З чого почати»
 * нижче й у переліку пакетів на цінах), а партнери — людина з плюсом:
 * до нашої команди додаються юристи суміжних галузей.
 */
const REASON_ICONS = ['fa-sitemap', 'fa-user-plus', 'fa-location-dot', 'fa-comment-dots'];
const reasons = whyUs.slice(1).map((label, i) => ({ icon: REASON_ICONS[i], label }));

// Перше речення візії й місії — сама теза, решта — пояснення до неї.
// indexOf, а не регулярний вираз із lookbehind: його не розбирає старий Safari.
const splitLead = (text) => {
  const end = text.indexOf('. ');
  return end === -1 ? [text, ''] : [text.slice(0, end + 1), text.slice(end + 2)];
};

const PRINCIPLES = [
  { id: 'viziya', ...vision },
  { id: 'misiya', ...mission }
];

/**
 * Порядок роботи — шкалою згори вниз, крок за кроком. Раніше п'ять карток
 * стояли сіткою 2×3, і п'ята лишалася сама з порожньою половиною ряду. Від
 * lg назва кроку й пояснення стоять поруч, тож кожен крок займає два рядки
 * тексту, а не картку.
 */
const Steps = () => (
  <ol className="list-none m-0 p-0">
    {howWeWork.map((step, i) => {
      const last = i === howWeWork.length - 1;
      return (
        <li key={step.label} className="relative flex gap-5 pb-8 last:pb-0 lg:gap-8">
          {!last && (
            <span className="absolute left-5 top-11 bottom-1 w-px bg-teal-200" aria-hidden="true"></span>
          )}
          <StepNumber n={i + 1} />
          <div className="min-w-0 flex-1 pt-2 lg:grid lg:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] lg:gap-8">
            <h3 className="text-lg font-semibold font-display text-slate-900 leading-snug">{step.label}</h3>
            <p className="mt-1.5 lg:mt-0 max-w-2xl leading-relaxed text-slate-600">{tie(step.text)}</p>
          </div>
        </li>
      );
    })}
  </ol>
);

/**
 * Візія й місія — поруч: це дві половини однієї думки. Від lg картки
 * ділять рядки спільної сітки (subgrid), тож тези, пояснення й переліки
 * починаються на одній висоті, а не «гуляють» через різну довжину тексту.
 * Раніше перелік притискався до низу картки, і у візії між абзацом і
 * переліком зяяла дірка.
 *
 * Картки — не окремі розділи, а частини розділу «Візія, місія й цінності»:
 * підпис-заголовок у них h3, і обгортка — div, а не section, щоб диктор не
 * оголошував кожну картку окремою областю сторінки.
 */
const Principles = () => (
  <div className="grid gap-5 lg:gap-x-6 lg:grid-cols-2">
    {PRINCIPLES.map((block) => {
      const [statement, rest] = splitLead(block.lead);
      return (
        <div
          key={block.id}
          className="grid content-start gap-y-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-8 lg:p-10 lg:row-span-4 lg:grid-rows-subgrid"
        >
          <Eyebrow as="h3" id={`${block.id}-title`} className="">
            {block.title}
          </Eyebrow>
          <p className="font-display font-bold text-xl sm:text-2xl leading-snug text-slate-900 text-balance">
            {tie(statement)}
          </p>
          {rest && <p className="text-base lg:text-lg leading-relaxed text-slate-600">{tie(rest)}</p>}
          <ul className="list-none mt-2 pt-6 border-t border-slate-200 space-y-4">
            {block.points.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-teal-600 flex-shrink-0" aria-hidden="true"></span>
                <span className="leading-relaxed text-slate-700">{tie(point)}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    })}
  </div>
);

/**
 * Цінності — окремою карткою під візією й місією, тією самою мовою: іконка
 * в квадраті, назва, речення. Номери 01–04 прибрано: порядок тут нічого не
 * означає. На телефоні — рядки з іконкою ліворуч замість чотирьох високих
 * карток, у кожній з яких було одне речення.
 *
 * «Наші цінності» — h3 у розділі, тож назви цінностей під ним — h4.
 */
const Values = () => (
  <div className="mt-5 lg:mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-8 lg:p-10">
    <Eyebrow as="h3" id="cinnosti-title" className="mb-6 lg:mb-8">
      Наші цінності
    </Eyebrow>
    <ul className="list-none grid gap-6 sm:grid-cols-2 sm:gap-x-8 xl:grid-cols-4">
      {values.map((value) => (
        <li key={value.title} className="flex items-start gap-4 xl:flex-col">
          <span
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"
            aria-hidden="true"
          >
            <Icon name={value.icon} className="text-lg" />
          </span>
          <div className="min-w-0 pt-1 xl:pt-0">
            <h4 className="text-lg font-bold font-display text-slate-900 leading-snug">{value.title}</h4>
            <p className="mt-1.5 leading-relaxed text-slate-600">{tie(value.description)}</p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// Блок з’являється лише тоді, коли в команді справді хтось описаний.
const Team = () => (
  <PageSection id="komanda" caption="Люди" title="Команда">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
      {team.map((person) => (
        <div key={person.name} className="h-full rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-bold text-slate-900 font-display">{person.name}</h3>
          <p className="text-sm text-teal-700 font-semibold mt-1">{person.role}</p>
          {person.focus && <p className="text-base text-slate-600 leading-relaxed mt-3">{person.focus}</p>}
          {person.credentials && (
            <p className="text-sm text-slate-600 leading-relaxed mt-3 pt-3 border-t border-slate-100">
              {person.credentials}
            </p>
          )}
        </div>
      ))}
    </div>
  </PageSection>
);

const buttonClass =
  'inline-flex items-center justify-center gap-2 min-h-[2.75rem] rounded-lg border border-slate-300 bg-white px-5 py-2.5 font-semibold text-slate-800 hover:border-teal-600 hover:text-teal-800 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2';

/**
 * Перехід для тих, хто ще не знає, яка послуга потрібна. Раніше — окрема
 * сіра панель із двома дрібними текстовими посиланнями, у які важко влучити
 * пальцем; тепер рядок із кнопками, як наприкінці сторінки контактів.
 */
const Start = () => (
  <section
    aria-labelledby="start-title"
    className="mt-14 lg:mt-20 rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
  >
    <div className="flex items-start gap-4 flex-1">
      <span
        className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"
        aria-hidden="true"
      >
        <Icon name="fa-sitemap" className="text-lg" />
      </span>
      <div>
        <h2 id="start-title" className="text-lg font-bold font-display text-slate-900">
          З чого почати
        </h2>
        <p className="mt-1 max-w-2xl text-slate-600 leading-relaxed">
          {tie('Якщо ви ще не знаєте, яка саме послуга вам потрібна, подивіться перелік напрямків або відповіді на часті запитання — здебільшого відповідь уже там.')}
        </p>
      </div>
    </div>
    {/* На телефоні кнопки на всю ширину одна під одною — у них легко
        влучити пальцем. */}
    <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
      <Link to="/poslugy" className={buttonClass}>
        Перелік послуг
      </Link>
      <Link to="/pytannya" className={buttonClass}>
        Часті запитання
        <Icon name="fa-arrow-right" className="text-sm" />
      </Link>
    </div>
  </section>
);

const AboutPage = () => {
  const schema = {
    '@type': 'AboutPage',
    inLanguage: 'uk-UA',
    mainEntity: {
      '@type': 'LegalService',
      name: 'Visia Medical Group',
      alternateName: 'VMG',
      url: SITE,
      description: companyLead[0],
      address: POSTAL_ADDRESS,
      areaServed: { '@type': 'Country', name: 'Україна' },
      knowsAbout: [
        'Медичне право',
        'Ліцензування медичної практики',
        'Електронна система охорони здоров’я (ЕСОЗ)',
        'Реєстрація медичного бізнесу',
        'Документація медичного закладу'
      ],
      ...(team.length
        ? { employee: team.map((m) => ({ '@type': 'Person', name: m.name, jobTitle: m.role })) }
        : {})
    }
  };

  return (
    <PageShell
      title="Про компанію: юристи з медичного права"
      description="Visia Medical Group — юридична компанія, для якої медичне право є основною спеціалізацією. Візія, місія, цінності та порядок роботи з клієнтом."
      path="/pro-kompaniyu"
      banner="/images/page-consult-e51e9060.jpg"
      schema={schema}
      crumbs={[{ name: 'Про компанію' }]}
      heading="Юристи, для яких медичне право — єдина спеціалізація"
      lead="Ми супроводжуємо медичні заклади від ідеї до сталої роботи: реєстрація, ліцензія МОЗ, ЕСОЗ, документація та щоденні правові питання."
      cta={{ source: 'about-page' }}
    >
      {/*
        Порядок — від конкретного до загального: хто ми й чим відрізняємося,
        що буде після звернення, а вже потім візія, місія й цінності. Раніше
        порядок роботи — те, про що людина питає першим, — стояв у самому
        кінці, на телефоні за п'ять екранів від банера.

        Великого фото лікарки посеред сторінки більше немає: на сторінці
        юридичної компанії воно нічого не казало про юристів, займало пів
        екрана, повторювало плитку «Переваг» на головній, а підпис під ним
        утретє переказував заголовок.
      */}
      <div className="page-container py-10 lg:py-14">
        <PageIntro
          paragraphs={companyLead}
          aside={{ title: 'Чому саме ми', items: reasons }}
        />

        {team.length > 0 && <Team />}

        <PageSection id="poryadok-roboty" caption="Порядок роботи" title="Як влаштована робота">
          <Steps />
        </PageSection>

        {/* Найбільший блок сторінки — зі звичайним заголовком розділу, як усі
            інші: раніше він починався одразу з карток, і в змісті для диктора
            між «Як влаштована робота» й «З чого почати» стояли три рівноправні
            h2-підписи. */}
        <PageSection id="khto-my" caption="Хто ми" title="Візія, місія й цінності">
          <Principles />
          <Values />
        </PageSection>

        <Start />
      </div>
    </PageShell>
  );
};

export default AboutPage;
